import { ref } from 'vue'
import type { RequestConfig } from 'yapi-to-typescript'
import type { Request } from './index'
import baseRequest from './request'
import dbCache from './requestDB'
import { addBreadcrumb, captureError } from '@/utils'

const enableCache = import.meta.env.VITE_ENABLE_CACHE_API === 'true'

type ApiHookConfig = {
  // 使能缓存
  invalidateCache?: boolean
  // 静默模式
  silent?: boolean
}

export default function makeRequestHook<
  TRequestData,
  TRequestConfig extends RequestConfig,
  TRequestResult extends ReturnType<typeof baseRequest>
>(request: Request<TRequestData, TRequestConfig, TRequestResult>) {
  type Data = TRequestResult extends Promise<infer R> ? R : TRequestResult
  return function useRequest(requestData?: TRequestData) {
    const data = ref<Data | null>(null)
    const loading = ref(false)
    const error = ref<string | null>(null)
    const isCacheData = ref(false)

    async function fetchFn(
      requestData: TRequestData,
      key: string | null,
      options: ApiHookConfig
    ) {
      try {
        const res = await request(requestData)
        if (
          request.requestConfig.method === 'GET' &&
          key &&
          enableCache &&
          !options.invalidateCache
        ) {
          dbCache.set(key, res)
        }
        data.value = res
        isCacheData.value = false
      } catch (err) {
        addBreadcrumb({
          message: 'API Hook 执行失败',
          category: 'api/hook',
          level: 'error',
          data: {
            path: request.requestConfig.path,
            method: request.requestConfig.method,
            hasCacheKey: Boolean(key)
          }
        })
        if (err instanceof Error) {
          captureError(err, {
            feature: 'api-makeRequestHook',
            path: request.requestConfig.path,
            method: request.requestConfig.method,
            requestData
          })
        }
        error.value = '数据加载失败，请稍后重试'
        data.value = null
        throw err
      } finally {
        // 只有在非静默模式时才设置loading为false
        // 静默模式下不改变loading状态，让轮询不影响用户界面
        if (!options.silent) {
          loading.value = false
        }
      }
    }

    async function execute(
      requestData: TRequestData,
      options?: ApiHookConfig
    ): Promise<Data | null> {
      const opt = {
        silent: !!options?.silent,
        invalidateCache: !!options?.invalidateCache
      }
      // 如果是静默模式（通常用于轮询），且已有数据，则不显示loading
      const shouldShowLoading = !opt.silent || data.value === null
      const shouldInvalidateCache = opt.invalidateCache

      if (shouldShowLoading) {
        loading.value = true
      }
      error.value = null
      let key: string | null = null
      if (
        request.requestConfig.method === 'GET' &&
        enableCache &&
        !shouldInvalidateCache
      ) {
        key = request.requestConfig.path + '-' + JSON.stringify(requestData)
        const res = await dbCache.get(key)
        if (res) {
          data.value = res
          if (shouldShowLoading) {
            loading.value = false
          }
          error.value = null
          isCacheData.value = true
        }
        Promise.resolve().then(() => {
          fetchFn(requestData, key, opt)
        })
      } else {
        await fetchFn(requestData, key, opt)
      }
      return data.value
    }

    // 当Get请求时,并且use hook中有请求参数, 则直接初始获取数据
    if (request.requestConfig.method === 'GET' && requestData) {
      execute(requestData)
    }

    return {
      // API请求相关
      data, // 请求返回的数据
      loading, // 请求加载状态
      error, // 请求错误信息
      isCacheData, // 是否使用的缓存数据
      execute // 执行请求的函数
    }
  }
}
